import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command/index";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { t } from "i18next";

interface ComboboxProps {
  items: { value: string; label: string }[];
  placeholder?: string;
  onValueChange: ({ value, label }: { value: string; label: string }) => void;
  value?: string;
}

export function Combobox({
  items,
  placeholder = t("components.combobox.commandEmpty"),
  onValueChange,
  value,
}: ComboboxProps): JSX.Element {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="flex flex-col">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            className="w-full text-base justify-between text-grey-400 bg-tinted-grey-1 h-11 border border-tinted-grey-2"
            role="combobox"
            variant="outline"
          >
            {items.find((item) => item.value === value)?.label || placeholder}
            <ChevronsUpDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command>
            <CommandInput className="h-9" placeholder={placeholder} />
            <CommandList>
              <CommandEmpty>
                {t("components.combobox.commandEmpty")}
              </CommandEmpty>
              <CommandGroup>
                {items.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.label}
                    onSelect={(currentValue) => {
                      const selectedItem = items.find(
                        (item) => item.label === currentValue
                      );
                      if (selectedItem) {
                        onValueChange({
                          value: selectedItem.value,
                          label: selectedItem.label,
                        });
                      }
                      setOpen(false);
                    }}
                  >
                    {item.label}
                    <Check
                      className={cn(
                        "ml-auto",
                        value === item.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
